import { useEffect } from 'react';
import Layout from '@/components/Layout'
import { TrackingScripts } from '@/components/TrackingScripts'
import { toastOptions } from '@/config/toast.config'
import { useFacebookPixel } from '@/hooks/useFacebookPixel'
import { useReferral } from '@/hooks/useReferral'
import '@/styles/globals.css'
import '@/styles/toast.css'
import { CartProvider } from '@/contexts/CartContext'
import { LoginModalProvider, useLoginModal } from '@/contexts/LoginModalContext' // ✅
import { PersonalizeModalProvider, usePersonalizeModal } from '@/contexts/PersonalizeModalContext'
import type { AppProps } from 'next/app'
import { GoogleAnalytics } from 'nextjs-google-analytics'
import React from 'react'
import { Toaster } from 'react-hot-toast'
import ToastLimiter from '@/utils/toastManager'
import { QueryClient, QueryClientProvider } from 'react-query'
import LoginModal from '@/components/Auth/LoginModal' // ✅ Add this import
import RegisterModal from '@/components/Auth/RegisterModal'
import ForgotModal from '@/components/Auth/ForgotModal'
import PersonalizeModal from '@/components/Personalize/PersonalizeModal'
import { CTAProvider } from '@/contexts/CTAContext'

const queryClient = new QueryClient()


function GlobalModals() {
  const {
    isLoginOpen,
    setIsLoginOpen,
    setIsRegisterOpen,
    isForgotOpen,
    setIsForgotOpen,
    setLastOpened,
    isRegisterOpen,
  } = useLoginModal();

  const { isOpen: isPersonalizeOpen, closeModal, productData } = usePersonalizeModal();

  // ✅ Combine modal open states
  const anyModalOpen = isLoginOpen || isRegisterOpen || isForgotOpen || isPersonalizeOpen;

  // ✅ Scroll lock logic
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const body = document.body;

    if (anyModalOpen) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }

    return () => {
      body.style.overflow = '';
    };
  }, [anyModalOpen]);

  return (
    <>
      <LoginModal
        isOpen={isLoginOpen}
        setIsLoginOpen={setIsLoginOpen}
        setIsRegisterOpen={setIsRegisterOpen}
        setIsForgotOpen={setIsForgotOpen}
        setLastOpened={setLastOpened}
      />

      {isRegisterOpen && (
        <RegisterModal
          isOpen={isRegisterOpen}
          setIsRegisterOpen={setIsRegisterOpen}
          setIsLoginOpen={setIsLoginOpen}
          setLastOpened={setLastOpened}
          setIsOpen={setIsRegisterOpen}
        />
      )}

      {isForgotOpen && (
        <ForgotModal
          isOpen={isForgotOpen}
          setIsOpen={setIsForgotOpen}
        />
      )}

      {isPersonalizeOpen && productData && (
        <PersonalizeModal
          setPersonalizeModal={closeModal}
          product={productData.product}
          inputs={productData.inputs}
        />
      )}
    </>
  );
}

export default function App({ Component, pageProps }: AppProps) {
  useFacebookPixel()
  useReferral()

  return (
    <>
      {process.env.NODE_ENV === 'production' && <TrackingScripts />}
      <link
        rel="stylesheet"
        crossOrigin="anonymous"
        href={process.env.NEXT_PUBLIC_SITE_URL + '/fonts.css'}
      />
      <QueryClientProvider client={queryClient}>
        <CartProvider>
          <PersonalizeModalProvider>
              <LoginModalProvider>
              <CTAProvider>
                <Layout>
                  <>
                    <Toaster position="top-center" toastOptions={toastOptions} />
                    <ToastLimiter />
                    {process.env.NODE_ENV === 'production' && <GoogleAnalytics trackPageViews />}
                    <Component {...pageProps} />
                    <GlobalModals /> {/* ✅ Mount login modal */}
                  </>
                </Layout>
              </CTAProvider>
            </LoginModalProvider>
          </PersonalizeModalProvider>
        </CartProvider>
      </QueryClientProvider>
    </>
  )
}
